import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Leaf, 
  Sparkles, 
  Heart, 
  Shield,
  Sun,
  Snowflake,
  Droplets,
  Activity,
  Flame,
  Cloud,
  Pill,
  Wind
} from 'lucide-react';
import { services, businessInfo, images } from '../data/mock';
import { CherryBlossom, BlossomBranch } from '../components/ui/FloralDecorations';

const ServicesPage = () => {
  const getIcon = (iconName) => {
    const icons = {
      leaf: Leaf,
      flower: Sparkles,
      pill: Pill,
      shield: Shield,
      sparkles: Sparkles,
      wind: Wind,
      heart: Heart,
      sun: Sun,
      snowflake: Snowflake,
      droplets: Droplets,
      armchair: Heart,
      activity: Activity,
      flame: Flame,
      cloud: Cloud
    };
    return icons[iconName] || Heart;
  };

  // Map service names to clinic images
  const serviceImages = {
    'Cryotherapy': images.cryotherapy,
    'Infrared Sauna': images.infraredSauna,
    'Massage Chair': images.massageChair
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #FCF0F0 0%, #F5E1E1 25%, white 50%, #E8C4C4 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute top-10 left-10 opacity-20 animate-float-slow">
          <CherryBlossom size={80} />
        </div>
        <div className="absolute bottom-10 right-10 opacity-15">
          <BlossomBranch flip />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-6 border border-[#D4A5A5]/30">
              <Heart size={16} style={{ color: '#D4A5A5' }} />
              <span className="text-sm font-medium text-[#2C2F36]">Comprehensive Care</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#2C2F36] mb-6">
              Our Services
            </h1>
            <p className="text-xl text-[#4A5568] mb-8 max-w-3xl mx-auto">
              From nutritional therapy consultations to wellness therapies, we offer a comprehensive 
              range of natural health services to support your wellness journey.
            </p>
          </div>
        </div>
      </section>

      {/* Core Care - Primary Focus */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(212, 165, 165, 0.2)', color: '#D4A5A5' }}>
              {services.coreCare.subtitle}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              {services.coreCare.title}
            </h2>
            <div className="floral-divider w-24 mb-6" />
            <p className="text-[#4A5568] max-w-2xl">
              Our core services focus on nutritional therapy as the foundation for healing, 
              supported by herbal remedies and high-quality supplements.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.coreCare.items.map((service) => {
              const IconComponent = getIcon(service.icon);
              return (
                <div 
                  key={service.id}
                  className="p-8 rounded-3xl card-elevated border border-[#D4A5A5]/10"
                  style={{ background: 'linear-gradient(180deg, white 0%, #FCF0F0 100%)' }}
                >
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ background: 'rgba(212, 165, 165, 0.15)' }}>
                    <IconComponent size={28} style={{ color: '#D4A5A5' }} />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-4">
                    {service.name}
                  </h3>
                  <p className="text-[#4A5568] leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link to="/nutritional-therapy" className="btn-pink">
              Learn More About Nutritional Therapy
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Detox & Restoration */}
      <section className="section-padding" style={{ background: 'linear-gradient(180deg, #FCF0F0 0%, #F5E1E1 50%, #FCF0F0 100%)' }}>
        <div className="container-custom">
          <div className="mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(160, 140, 168, 0.2)', color: '#A08CA8' }}>
              {services.detox.subtitle}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              {services.detox.title}
            </h2>
            <div className="floral-divider w-24 mb-6" />
            <p className="text-[#4A5568] max-w-2xl">
              Our comprehensive detoxification support helps address parasites, heavy metals, 
              environmental chemicals, and microbial imbalances.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.detox.items.map((service) => {
              const IconComponent = getIcon(service.icon);
              return (
                <div 
                  key={service.id}
                  className="bg-white p-6 rounded-2xl card-elevated"
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(160, 140, 168, 0.15)' }}>
                    <IconComponent size={24} style={{ color: '#A08CA8' }} />
                  </div>
                  <h3 className="font-serif text-lg font-medium text-[#2C2F36] mb-3">
                    {service.name}
                  </h3>
                  <p className="text-sm text-[#4A5568]">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link to="/detox-support" className="btn-secondary">
              Learn More About Detox Support
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Wellness Therapies with Images */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(110, 158, 185, 0.15)', color: '#6E9EB9' }}>
              {services.wellness.subtitle}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              {services.wellness.title}
            </h2>
            <div className="floral-divider w-24 mb-6" />
            <p className="text-[#4A5568] max-w-2xl">
              Complement your healing journey with our range of supportive wellness therapies, 
              designed to enhance relaxation and support your body's natural processes.
            </p>
          </div>

          {/* Featured therapies with images */}
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={images.cryotherapy}
                  alt="Cryotherapy Chamber"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-2">Cryotherapy</h3>
                <p className="text-sm text-[#4A5568]">Cold therapy to support inflammation reduction and recovery.</p>
              </div>
            </div>
            
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={images.infraredSauna}
                  alt="Infrared Sauna"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-2">Infrared Sauna</h3>
                <p className="text-sm text-[#4A5568]">Deep heat therapy to support detoxification and relaxation.</p>
              </div>
            </div>
            
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={images.massageChair}
                  alt="Massage Chair"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-2">Massage Chair</h3>
                <p className="text-sm text-[#4A5568]">Therapeutic massage for relaxation and stress relief.</p>
              </div>
            </div>
          </div>

          {/* Other wellness services */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {services.wellness.items.filter(s => !['Cryotherapy', 'Infrared Sauna', 'Massage Chair'].includes(s.name)).map((service) => {
              const IconComponent = getIcon(service.icon);
              return (
                <div 
                  key={service.id}
                  className="flex items-start gap-4 p-5 rounded-2xl transition-all duration-300 hover:shadow-md border border-transparent hover:border-[#D4A5A5]/20"
                  style={{ background: 'linear-gradient(135deg, #FCF0F0 0%, white 100%)' }}
                >
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                    <IconComponent size={20} style={{ color: '#6E9EB9' }} />
                  </div>
                  <div>
                    <h3 className="font-serif text-base font-medium text-[#2C2F36] mb-1">
                      {service.name}
                    </h3>
                    <p className="text-xs text-[#4A5568]">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding" style={{ background: 'linear-gradient(180deg, #FCF0F0 0%, white 100%)' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(212, 165, 165, 0.2)', color: '#D4A5A5' }}>
              Getting Started
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              How to Begin Your Wellness Journey
            </h2>
            <div className="floral-divider w-24 mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-serif font-bold text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #D4A5A5, #E8C4C4)' }}>
                1
              </div>
              <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-3">
                Attend Orientation
              </h3>
              <p className="text-[#4A5568]">
                Join our free orientation class to learn about our approach and meet Julie.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-serif font-bold text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #A08CA8, #C4B5CB)' }}>
                2
              </div>
              <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-3">
                Schedule Consultation
              </h3>
              <p className="text-[#4A5568]">
                Book your nutritional therapy consultation for a personalized assessment.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-serif font-bold text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #6E9EB9, #93A8C7)' }}>
                3
              </div>
              <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-3">
                Add Therapies
              </h3>
              <p className="text-[#4A5568]">
                Enhance your protocol with complementary wellness therapies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #D4A5A5 0%, #E8C4C4 50%, #D4A5A5 100%)' }}>
        <div className="absolute top-10 left-10 opacity-20 animate-float">
          <CherryBlossom size={60} color="white" />
        </div>
        <div className="absolute bottom-10 right-10 opacity-15 animate-float-slow">
          <CherryBlossom size={80} color="white" />
        </div>
        
        <div className="container-custom text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6 text-white">
            Ready to Experience Our Services?
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-8">
            Start your wellness journey with a free orientation class or schedule 
            your personalized consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-white text-[#D4A5A5] rounded-full font-semibold hover:bg-white/90 transition-all hover:scale-105 inline-flex items-center justify-center gap-2 shadow-lg"
            >
              Book Consultation
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/orientation" 
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              Free Orientation Class
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;